import { create } from 'zustand';

/**
 * Quick Favorites (Blender's Q menu): commands the user starred, opened as a
 * menu under the mouse. Stored per user as command ids, so a favorite follows
 * the command wherever its key binding goes.
 */

const KEY = 'ai-studio:quick-favorites';
const DEFAULTS = ['search.open', 'view.history', 'layout.maximize', 'view.shortcuts'];

function read(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as unknown;
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function write(ids: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    // not remembered
  }
}

interface FavoritesState {
  ids: string[];
  /** Menu position while the Q menu is open. */
  menu: { x: number; y: number } | null;
  toggle: (id: string) => void;
  has: (id: string) => boolean;
  move: (id: string, delta: -1 | 1) => void;
  open: (at: { x: number; y: number }) => void;
  close: () => void;
}

export const useFavorites = create<FavoritesState>((set, get) => ({
  ids: typeof localStorage === 'undefined' ? DEFAULTS : read(),
  menu: null,
  toggle: (id) => {
    const ids = get().ids.includes(id) ? get().ids.filter((x) => x !== id) : [...get().ids, id];
    set({ ids });
    write(ids);
  },
  has: (id) => get().ids.includes(id),
  move: (id, delta) => {
    const ids = [...get().ids];
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    set({ ids });
    write(ids);
  },
  open: (at) => set({ menu: at }),
  close: () => set({ menu: null }),
}));

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) useFavorites.setState({ ids: read() });
  });
}
